import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '../../common/Input'
import { Button } from '../../common/Button'
import { StepHeader } from '../StepHeader'

interface LovedOnePhoneStepProps {
  initialValue?: string
  onNext: (value: string) => void
  onBack: () => void
}

/**
 * Format phone number as user types: (XXX)XXX-XXXX
 */
function formatPhoneNumber(value: string): string {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '')

  // Limit to 10 digits
  const limited = digits.substring(0, 10)

  // Format as (XXX)XXX-XXXX
  if (limited.length === 0) return ''
  if (limited.length <= 3) return `(${limited}`
  if (limited.length <= 6) return `(${limited.slice(0, 3)})${limited.slice(3)}`
  return `(${limited.slice(0, 3)})${limited.slice(3, 6)}-${limited.slice(6)}`
}

/**
 * Loved one phone number collection and validation (FR-012, FR-019)
 */
export function LovedOnePhoneStep({ initialValue = '', onNext, onBack }: LovedOnePhoneStepProps) {
  const { t } = useTranslation()
  // Convert E.164 format back to display format if needed
  const displayValue = initialValue.startsWith('+1')
    ? formatPhoneNumber(initialValue.slice(2))
    : formatPhoneNumber(initialValue)
  const [phone, setPhone] = useState(displayValue)
  const [error, setError] = useState('')

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value)
    setPhone(formatted)
    if (error) setError('')
  }

  const handleNext = () => {
    // Extract digits for validation
    const digits = phone.replace(/\D/g, '')

    if (digits.length === 0) {
      setError(t('errors.required'))
      return
    }

    if (digits.length !== 10) {
      setError('Please enter a valid 10-digit US phone number')
      return
    }

    // Format as E.164 for storage: +1XXXXXXXXXX
    const e164 = `+1${digits}`
    onNext(e164)
  }

  return (
    <div>
      <StepHeader type="loved-one" title="What is their phone number?" />

      <div
        style={{
          marginBottom: '1rem',
          padding: '12px',
          backgroundColor: '#eff6ff',
          borderRadius: '8px',
          fontSize: '14px',
          color: '#1e40af',
        }}
      >
        🇺🇸 US phone numbers only
      </div>

      <Input
        label={t('onboarding.lovedOne.phoneLabel')}
        placeholder="(555)123-4567"
        type="tel"
        value={phone}
        onChange={handlePhoneChange}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault()
            handleNext()
          }
        }}
        error={error}
        helpText={t('onboarding.lovedOne.phoneHelp')}
        prefix="+1"
        autoFocus
      />

      <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
        <Button variant="outline" onClick={onBack} style={{ flex: 1 }}>
          {t('onboarding.navigation.back')}
        </Button>
        <Button onClick={handleNext} style={{ flex: 1 }}>
          {t('onboarding.navigation.next')}
        </Button>
      </div>
    </div>
  )
}
